import React from "react"
import { Box, SimpleGrid, VStack } from "@chakra-ui/layout"
import { Divider, Heading, Text } from "@chakra-ui/react"

// ─── Types ────────────────────────────────────────────────────────────────────

export type BenefitRow = {
  timeQualifier: string
  benefitAmount: string | undefined
  dates: string
}

export type CoverageGroups = { [coverageLevel: string]: BenefitRow[] }
export type ServiceTypeGroups = { [serviceType: string]: CoverageGroups }
export type NetworkGroups = { [network: string]: ServiceTypeGroups }

type RawBenefit = {
  code: string
  coverageLevel?: string
  inPlanNetworkIndicator?: string
  serviceTypes?: string[]
  timeQualifier?: string
  benefitAmount?: string
  benefitsDateInformation?: { plan?: string; planBegin?: string; benefit?: string }
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function formatDateRange(dateRange: string | undefined): string {
  if (!dateRange) return "—"
  // API returns compact YYYYMMDD-YYYYMMDD format (no hyphens within each date part)
  const [start, end] = dateRange.split("-")
  const fmt = (d: string) => {
    if (!d) return "—"
    if (d === "99991231") return "ongoing"
    return `${d.slice(4, 6)}/${d.slice(6, 8)}/${d.slice(0, 4)}`
  }
  return end ? `${fmt(start)} - ${fmt(end)}` : fmt(start)
}

export function formatAmount(amount: string | undefined): string {
  if (amount == null) return "—"
  const num = parseFloat(amount)
  if (isNaN(num)) return amount
  const hasFraction = num % 1 !== 0
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: hasFraction ? 2 : 0, maximumFractionDigits: 2 })}`
}

export function groupBenefits(
  benefitsInformation: RawBenefit[],
  code: string,
  dateKey: "plan" | "benefit"
): NetworkGroups {
  const result: NetworkGroups = {}

  for (const b of benefitsInformation) {
    if (b.code !== code) continue

    const network = b.inPlanNetworkIndicator ?? "Not Applicable"
    const serviceType = (b.serviceTypes ?? []).join(", ") || "General"
    const coverageLevel = b.coverageLevel ?? "General"

    result[network] ??= {}
    result[network][serviceType] ??= {}
    result[network][serviceType][coverageLevel] ??= []

    const dateInfo = b.benefitsDateInformation
    const dates = formatDateRange(
      dateKey === "plan" ? dateInfo?.plan ?? dateInfo?.planBegin : dateInfo?.benefit ?? dateInfo?.planBegin
    )
    result[network][serviceType][coverageLevel].push({
      timeQualifier: b.timeQualifier ?? "—",
      benefitAmount: b.benefitAmount,
      dates
    })
  }

  return result
}

// ─── Internal constants ───────────────────────────────────────────────────────

const NETWORK_ORDER = ["Yes", "No", "Not Applicable"]
const NETWORK_LABELS: Record<string, string> = {
  Yes: "In-Network",
  No: "Out-of-Network",
  "Not Applicable": "Not Applicable"
}
const COVERAGE_ORDER = ["Individual", "Family"]

function sortCoverageLevels(levels: string[]): string[] {
  return [...levels].sort((a, b) => {
    const ai = COVERAGE_ORDER.indexOf(a)
    const bi = COVERAGE_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function BenefitSummarySection({
  sectionTitle,
  code,
  benefits,
  dateKey
}: {
  sectionTitle: string
  code: string
  benefits: RawBenefit[]
  dateKey: "plan" | "benefit"
}): JSX.Element {
  const grouped = groupBenefits(benefits, code, dateKey)
  const networks = Object.keys(grouped).sort((a, b) => NETWORK_ORDER.indexOf(a) - NETWORK_ORDER.indexOf(b))

  if (networks.length === 0) {
    return (
      <Box>
        <Heading size="sm" mb={2}>
          {sectionTitle}
        </Heading>
        <Text color="gray.500">None found</Text>
      </Box>
    )
  }

  return (
    <Box>
      <Heading size="sm" mb={3}>
        {sectionTitle}
      </Heading>
      <VStack align="stretch" spacing={4}>
        {networks.map((network) => (
          <Box key={network} pl={4}>
            <Heading size="xs" mb={2}>
              {NETWORK_LABELS[network] ?? network} {sectionTitle}
            </Heading>
            <VStack align="stretch" spacing={3} pl={4}>
              {Object.entries(grouped[network]).map(([serviceType, coverageLevels]) => (
                <Box key={serviceType}>
                  <Text fontSize="xs" fontWeight="semibold" mb={1}>
                    {serviceType}
                  </Text>
                  <VStack align="stretch" spacing={2} pl={4}>
                    {sortCoverageLevels(Object.keys(coverageLevels)).map((level) => (
                      <Box key={level}>
                        <Text fontSize="xs" fontWeight="bold">
                          {level}
                        </Text>
                        <VStack align="stretch" pl={3}>
                          {coverageLevels[level].map((row, i) => (
                            <Text key={`${row.timeQualifier}-${i}`} fontSize="s">
                              {row.timeQualifier}: {formatAmount(row.benefitAmount)}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

type DeductibleOopSummaryProps = {
  response: any
}

export default function DeductibleOopSummary({ response }: DeductibleOopSummaryProps): JSX.Element {
  const planName = (response.planStatus as any[] | undefined)?.find((s) => s.planDetails)?.planDetails
  const payerName = response.payer?.name
  const planDateInfo = response.planDateInformation
  const planDateRange =
    planDateInfo?.plan ??
    (planDateInfo?.planBegin && planDateInfo?.planEnd
      ? `${planDateInfo.planBegin}-${planDateInfo.planEnd}`
      : planDateInfo?.planBegin)
  const planDates = formatDateRange(planDateRange)
  const benefits: RawBenefit[] = response.benefitsInformation ?? []
  const coverageLevel = benefits.find((b) => b.code === "1")?.coverageLevel

  return (
    <VStack align="stretch" spacing={6} p={2}>
      <Box>
        {planName && (
          <Heading size="sm" mb={1}>
            {planName}
          </Heading>
        )}
        {payerName && payerName.toLowerCase() !== "unknown" && <Text color="gray.600">{payerName}</Text>}
        <Text fontSize="sm">Plan dates: {planDateRange ? planDates : "check individual benefits"}</Text>
        {coverageLevel && <Text fontSize="sm">Coverage level: {coverageLevel}</Text>}
      </Box>

      <Divider />

      <SimpleGrid columns={2} spacing={6}>
        <BenefitSummarySection sectionTitle="Deductible" code="C" benefits={benefits} dateKey="plan" />
        <BenefitSummarySection sectionTitle="OOP Maximum" code="G" benefits={benefits} dateKey="benefit" />
      </SimpleGrid>
    </VStack>
  )
}
